import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SERVICES, ADDONS, MUSIC_OPTIONS, BRAND, IMAGES } from "@/lib/constants";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONV_LEVELS = [
  { v: 0, key: "quiet", label: "Quiet", hint: "No conversation, please." },
  { v: 1, key: "minimal", label: "Minimal", hint: "A polite hello, then silence." },
  { v: 2, key: "chatty", label: "Chatty", hint: "I'm in the mood to chat." },
];

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialService = params.get("service") || "airport";
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    service_type: initialService,
    full_name: "",
    email: "",
    phone: "",
    pickup_location: "",
    dropoff_location: "",
    pickup_date: "",
    pickup_time: "",
    passengers: 1,
  });
  const [conv, setConv] = useState(1);
  const [music, setMusic] = useState("driver_choice");
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [comments, setComments] = useState("");
  const [addons, setAddons] = useState([]);
  const [stops, setStops] = useState([]);

  const STOP_FEE = 20;
  const stopsCount = stops.filter((s) => s && s.trim()).length;
  const stopsTotal = stopsCount * STOP_FEE;

  const service = useMemo(
    () => SERVICES.find((s) => s.id === form.service_type) || SERVICES[0],
    [form.service_type]
  );
  const addonsTotal = addons.reduce(
    (sum, id) => sum + (ADDONS.find((a) => a.id === id)?.price || 0),
    0
  );
  const total = service.price + addonsTotal + stopsTotal;

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAddon = (id) =>
    setAddons((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const addStop = () => setStops((s) => [...s, ""]);
  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));
  const updateStop = (i, v) => setStops((s) => s.map((x, idx) => (idx === i ? v : x)));

  const validateStep1 = () => {
    if (!form.service_type) return "Choose a service";
    if (!form.pickup_location || !form.dropoff_location) return "Pickup and drop-off are required";
    if (!form.pickup_date || !form.pickup_time) return "Date and time are required";
    return null;
  };
  const validateStep4 = () => {
    if (!form.full_name) return "Your name is required";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Valid email required";
    if (!form.phone) return "Phone number required";
    return null;
  };

  const next = () => {
    if (step === 1) {
      const err = validateStep1();
      if (err) return toast.error(err);
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    const err = validateStep4();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        passengers: Number(form.passengers) || 1,
        mood: {
          conversation_level: CONV_LEVELS[conv].key,
          music_preference: music,
          playlist_url: playlistUrl || null,
          special_comments: comments || null,
        },
        addons,
        additional_stops: stops.filter((s) => s && s.trim()),
      };
      const { data: booking } = await axios.post(`${API}/bookings`, payload);
      const origin = window.location.origin;
      const { data: session } = await axios.post(`${API}/checkout/session`, {
        booking_id: booking.id,
        origin_url: origin,
      });
      window.location.href = session.url;
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <section className="relative pt-32 pb-24 min-h-screen" data-testid="booking-page">
        <div className="absolute top-0 inset-x-0 h-[420px] -z-10 opacity-30">
          <img src={IMAGES.interior} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/85 to-void" />
        </div>

        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="overline mb-4">Reserve · Step {step} of 4</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-12" data-testid="booking-title">
            Build the <em className="text-gold">ride</em>.
          </h1>

          {/* Progress */}
          <div className="flex items-center gap-3 mb-14">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className={`h-px flex-1 ${n <= step ? "bg-gold" : "bg-[rgba(212,175,55,0.18)]"}`} />
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-10">
              {step === 1 && (
                <div className="space-y-8" data-testid="step-trip-details">
                  <div>
                    <label className="overline block mb-3">Choose Service</label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {SERVICES.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => update("service_type", s.id)}
                          className={`text-left border p-5 transition ${form.service_type === s.id ? "border-gold bg-emerald-deep" : "border-gold-soft hover:border-gold"}`}
                          data-testid={`select-service-${s.id}`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="font-serif-display text-xl text-ivory">{s.title}</div>
                            <div className="text-gold text-xs tracking-widest">STARTING&nbsp;AT&nbsp;${s.price}</div>
                          </div>
                          <div className="text-ivory-muted text-xs">{s.blurb.slice(0, 70)}…</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Pickup Location" testid="input-pickup">
                      <input className="luxe-input w-full" value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} placeholder="123 Main St, Pittsburgh" data-testid="input-pickup" />
                    </Field>
                    <Field label="Drop-Off Location" testid="input-dropoff">
                      <input className="luxe-input w-full" value={form.dropoff_location} onChange={(e) => update("dropoff_location", e.target.value)} placeholder="PIT Airport" data-testid="input-dropoff" />
                    </Field>
                    <Field label="Pickup Date" testid="input-date">
                      <input type="date" className="luxe-input w-full" value={form.pickup_date} onChange={(e) => update("pickup_date", e.target.value)} data-testid="input-date" />
                    </Field>
                    <Field label="Pickup Time" testid="input-time">
                      <input type="time" className="luxe-input w-full" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} data-testid="input-time" />
                    </Field>
                    <Field label="Passengers" testid="input-passengers">
                      <input type="number" min="1" max="7" className="luxe-input w-full" value={form.passengers} onChange={(e) => update("passengers", e.target.value)} data-testid="input-passengers" />
                    </Field>
                  </div>

                  {/* Multi-Stop Add-On */}
                  <div className="border border-gold-soft p-6 bg-emerald-deep" data-testid="multi-stop-box">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <div className="overline mb-1">Additional Stops</div>
                        <div className="font-serif-display text-2xl text-ivory leading-snug">Need to make a stop along the way?</div>
                        <div className="text-ivory-muted text-sm mt-1">Florist, drinks pickup, picking up a friend — add as many stops as you like. <span className="text-gold">+${STOP_FEE} per stop.</span></div>
                      </div>
                      {stopsCount > 0 && (
                        <div className="text-right shrink-0">
                          <div className="overline" style={{fontSize: "0.6rem"}}>Stops total</div>
                          <div className="font-serif-display text-2xl text-gold">+${stopsTotal}</div>
                        </div>
                      )}
                    </div>

                    {stops.length > 0 && (
                      <div className="space-y-3 mt-5">
                        {stops.map((s, i) => (
                          <div key={i} className="flex items-center gap-3" data-testid={`stop-row-${i}`}>
                            <div className="overline shrink-0 w-8" style={{fontSize: "0.65rem"}}>#{i + 1}</div>
                            <input
                              className="luxe-input flex-1"
                              value={s}
                              onChange={(e) => updateStop(i, e.target.value)}
                              placeholder={`Stop address (e.g., florist, friend's place)`}
                              data-testid={`stop-input-${i}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeStop(i)}
                              className="h-[52px] px-4 border border-gold-soft text-ivory-muted hover:text-gold hover:border-gold transition text-xs tracking-widest uppercase shrink-0"
                              data-testid={`stop-remove-${i}`}
                              aria-label={`Remove stop ${i + 1}`}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={addStop}
                      className="mt-5 inline-flex items-center gap-2 text-gold text-xs tracking-[0.25em] uppercase hover:gap-3 transition-all"
                      data-testid="add-stop-btn"
                    >
                      <span className="text-base">+</span> Add a stop
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-12" data-testid="step-mood">
                  <div>
                    <label className="overline block mb-4">Conversation Level</label>
                    <div className="border border-gold-soft p-8 bg-emerald-deep">
                      <div className="font-serif-display text-2xl text-ivory mb-2">{CONV_LEVELS[conv].label}</div>
                      <div className="text-ivory-muted text-sm mb-8">{CONV_LEVELS[conv].hint}</div>
                      <Slider
                        value={[conv]}
                        onValueChange={(v) => setConv(v[0])}
                        min={0}
                        max={2}
                        step={1}
                        className="my-4"
                        data-testid="slider-conv"
                      />
                      <div className="flex justify-between text-xs text-ivory-muted mt-3 tracking-widest">
                        <span>QUIET</span><span>MINIMAL</span><span>CHATTY</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="overline block mb-4">Music Preference</label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {MUSIC_OPTIONS.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setMusic(m.id)}
                          className={`text-left border px-4 py-4 transition text-sm ${music === m.id ? "border-gold bg-emerald-deep text-ivory" : "border-gold-soft text-ivory-muted hover:text-ivory hover:border-gold"}`}
                          data-testid={`music-${m.id}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    {music === "playlist" && (
                      <div className="mt-4">
                        <input className="luxe-input w-full" value={playlistUrl} onChange={(e) => setPlaylistUrl(e.target.value)} placeholder="Paste your Spotify or Apple Music playlist link" data-testid="input-playlist" />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="overline block mb-4">Special Comments</label>
                    <textarea
                      rows={4}
                      className="luxe-textarea w-full"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Running late? Surprising someone? Allergic to perfume? Tell us anything that helps us serve you better."
                      data-testid="input-comments"
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6" data-testid="step-addons">
                  <div className="overline">Premium Add-Ons</div>
                  <p className="text-ivory-muted text-sm leading-relaxed max-w-xl">Optional touches we'll have ready inside the cabin upon pickup.</p>

                  <div className="space-y-3">
                    {ADDONS.map((a) => {
                      const checked = addons.includes(a.id);
                      return (
                        <button
                          key={a.id}
                          onClick={() => toggleAddon(a.id)}
                          className={`w-full flex items-center justify-between border p-5 transition text-left ${checked ? "border-gold bg-emerald-deep" : "border-gold-soft hover:border-gold"}`}
                          data-testid={`addon-${a.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`h-5 w-5 mt-0.5 border ${checked ? "bg-gold border-gold" : "border-gold-soft"} flex items-center justify-center`}>
                              {checked && <Check className="h-3 w-3 text-void" strokeWidth={3} />}
                            </div>
                            <div>
                              <div className="font-serif-display text-xl text-ivory">{a.label}</div>
                              <div className="text-ivory-muted text-xs">{a.hint}</div>
                            </div>
                          </div>
                          <div className="text-gold text-sm">+${a.price}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-8" data-testid="step-contact">
                  <div className="overline">Your Details</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name">
                      <input className="luxe-input w-full" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Jordan Williams" data-testid="input-name" />
                    </Field>
                    <Field label="Phone">
                      <input className="luxe-input w-full" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(412) 555-0123" data-testid="input-phone" />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Email">
                        <input type="email" className="luxe-input w-full" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" data-testid="input-email" />
                      </Field>
                    </div>
                  </div>

                  <div className="border border-gold-soft p-6 bg-emerald-deep text-sm text-ivory-muted leading-relaxed">
                    <div className="overline mb-3">A note before you confirm</div>
                    Your card will be charged the full amount upon confirmation. Cancellations 48+ hours in advance are fully refundable. Within 48 hours, a 50% retainer applies. We always work with you on rescheduling — call <a href={`tel:${BRAND.phone}`} className="text-gold">{BRAND.phoneVanity}</a>.
                  </div>
                </div>
              )}

              {/* Nav buttons */}
              <div className="flex items-center justify-between pt-8 border-t border-gold-soft">
                {step > 1 ? (
                  <button onClick={prev} className="btn-ghost-gold px-7 py-3 inline-flex items-center gap-2" data-testid="btn-back"><ChevronLeft className="h-4 w-4" /> Back</button>
                ) : <Link to="/" className="btn-ghost-gold px-7 py-3 inline-flex items-center gap-2" data-testid="btn-home"><ChevronLeft className="h-4 w-4" /> Home</Link>}
                {step < 4 ? (
                  <button onClick={next} className="btn-gold px-9 py-3 inline-flex items-center gap-2" data-testid="btn-next">Continue <ChevronRight className="h-4 w-4" /></button>
                ) : (
                  <button onClick={submit} disabled={submitting} className="btn-gold px-9 py-3 inline-flex items-center gap-2 disabled:opacity-50" data-testid="btn-checkout">
                    {submitting ? "Redirecting…" : "Confirm & Pay"} <ArrowUpRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Summary sidebar */}
            <aside className="lg:col-span-1">
              <div className="border border-gold-soft bg-emerald-deep p-7 sticky top-28" data-testid="booking-summary">
                <div className="overline mb-4">Reservation</div>
                <div className="font-serif-display text-3xl text-ivory leading-tight mb-6">{service.title}</div>

                <ul className="space-y-3 text-sm text-ivory-muted mb-6">
                  <Row label="Starting price" value={`$${service.price.toFixed(2)}`} />
                  {addons.map((id) => {
                    const a = ADDONS.find((x) => x.id === id);
                    return <Row key={id} label={a.label} value={`+$${a.price.toFixed(2)}`} />;
                  })}
                  {stopsCount > 0 && (
                    <Row label={`${stopsCount} additional stop${stopsCount > 1 ? "s" : ""}`} value={`+$${stopsTotal.toFixed(2)}`} />
                  )}
                  {form.pickup_date && <Row label="Date" value={`${form.pickup_date} ${form.pickup_time}`} />}
                  {form.pickup_location && <Row label="Pickup" value={form.pickup_location} truncate />}
                  {form.dropoff_location && <Row label="Drop-off" value={form.dropoff_location} truncate />}
                </ul>

                <div className="gold-line my-5" />
                <div className="flex items-baseline justify-between">
                  <div className="overline">Starting Total</div>
                  <div className="font-serif-display text-3xl text-gold" data-testid="total-amount">${total.toFixed(2)}</div>
                </div>
                <div className="text-[11px] text-ivory-muted mt-2 tracking-widest uppercase">Starts here · Final fare adjusts with mileage · Card secured by Stripe</div>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.25em] text-ivory-muted mb-2">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value, truncate }) {
  return (
    <li className="flex justify-between gap-3">
      <span className="opacity-70">{label}</span>
      <span className={`text-ivory ${truncate ? "truncate max-w-[160px]" : ""} text-right`}>{value}</span>
    </li>
  );
}
